import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Nav,
  FloatingLabel,
  Spinner,
} from "react-bootstrap";
import {
  Map,
  Smartphone,
  Mail,
  Clock,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
} from "react-feather";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { motion } from "framer-motion";

import apiService from "../../services/apiService";
import { useNotification } from "../../components/notification";

import Location from "../../resources/img/location.png";

// Yup validation schema
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phone: yup
    .string()
    .required("Phone number is required")
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
  subject: yup.string().required("Subject is required"),
  message: yup.string().required("Message is required"),
});

const ContactUs = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema),
  });

  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      await apiService.post("/email/contact-us", data);
      showNotification("success", "Message sent successfully!", 5000); // Success message
      reset();
    } catch (error) {
      showNotification("danger", error.message || "Failed to send message!", 5000); // Error message
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container fluid className="contact-us p-0 site-content">
      <Row className="banner justify-content-center align-items-center">
        <Col lg={8} className="text-center">
          <motion.h1
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="fw-bold mb-1 title text-white"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="fs-5 text-white"
          >
            We're here to help
          </motion.p>
        </Col>
      </Row>

      <Container className="my-5">
        <Row className="justify-content-center">
          <Col lg={11} className="bg-white p-5">
            <Row className="align-items-center">
              <Col lg={5}>
                <h2 className="mb-2 title">Get in Touch with us</h2>
                <p className="mb-0 fs-6">
                  Let's talk! Reach out to us for any inquiries or support.
                </p>
                <p className="mb-4 fs-6">
                  Tell us what you need. We’ll get back to you within 48 hours.
                </p>
                <div className="d-flex align-items-center mb-3 text-light-emphasis">
                  <Map size={22} className="me-3" />
                  No.76 B, GKS Estates, Block B, Ambattur Industrial Estate,{" "}
                  <br /> Chennai - 600 098, India ccc@sheenlac.in
                </div>
                <div className="d-flex align-items-center mb-3 text-light-emphasis">
                  <Smartphone size={22} className="me-3" />
                  <a
                    href="tel:080-47493330"
                    className="me-3 text-decoration-none link-secondary"
                  >
                    080-47493330,
                  </a>
                  <a
                    href="tel:+91 83000 30404"
                    className="me-3 text-decoration-none link-secondary"
                  >
                    +91 83000 30404 | 05 05
                  </a>
                </div>
                <div className="d-flex align-items-center mb-3 text-light-emphasis">
                  <Mail size={22} className="me-3" />
                  <a
                    href="mailto:ccc@sheenlac.in"
                    className="me-3 text-decoration-none link-secondary"
                  >
                    ccc@sheenlac.in
                  </a>
                </div>
                <div className="d-flex align-items-center mb-3 text-light-emphasis">
                  <Clock size={22} className="me-3" />
                  Monday to Saturday: 9.00 AM to 5.30 PM
                </div>
                <hr />
                <div className="d-flex align-items-center mb-3">
                  <span className="fw-semibold">Stay connected with us:</span>
                  <Nav className="justify-content-center align-items-center fw-light">
                    <Nav.Item>
                      <Nav.Link href="https://www.facebook.com/sheenlacpaints/">
                        <Facebook size={22} className="text-secondary" />
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link href="https://x.com/sheenlacpaints">
                        <Twitter size={22} className="text-secondary" />
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link href="https://www.instagram.com/sheenlacpaintsofficial/?hl=en">
                        <Instagram size={22} className="text-secondary" />
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="https://www.youtube.com/channel/UC_-0ObnmKnG6MHdFS1HgCMw">
                        <Youtube size={24} className="text-secondary" />
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>
                <hr />

                <motion.img
                  src={Location}
                  alt="Location"
                  className="img-fluid rounded-2"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                />
              </Col>
              <Col lg={1}>
                <div className="vr vh-100 mx-auto d-none d-lg-block"></div>
              </Col>
              <Col lg={6} className="mt-4 mt-lg-0">
                <p className="mb-0 fs-4 title">Leave a Message</p>
                <h2 className="mb-4 title">We love to hear from you</h2>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <FloatingLabel controlId="name" label="Name" className="mb-3">
                    <Controller
                      name="name"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          placeholder="Your Name"
                          isInvalid={!!errors.name}
                        />
                      )}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name?.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <FloatingLabel
                    controlId="email"
                    label="Email"
                    className="mb-3"
                  >
                    <Controller
                      name="email"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          placeholder="Email"
                          isInvalid={!!errors.email}
                        />
                      )}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email?.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <FloatingLabel
                    controlId="phone"
                    label="Phone Number"
                    className="mb-3"
                  >
                    <Controller
                      name="phone"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          placeholder="Phone Number"
                          isInvalid={!!errors.phone}
                        />
                      )}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone?.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <FloatingLabel
                    controlId="subject"
                    label="Subject"
                    className="mb-3"
                  >
                    <Controller
                      name="subject"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          placeholder="Subject"
                          isInvalid={!!errors.subject}
                        />
                      )}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.subject?.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <FloatingLabel
                    controlId="message"
                    label="Message"
                    className="mb-3"
                  >
                    <Controller
                      name="message"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          as="textarea"
                          rows={4}
                          placeholder="Message"
                          isInvalid={!!errors.message}
                        />
                      )}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.message?.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <div className="text-end mt-4">
                    <Button
                      variant="primary"
                      disabled={isLoading}
                      type="submit"
                      className="px-5 py-3"
                    >
                      {isLoading ? (
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      ) : (
                        "SEND MESSAGE"
                      )}
                    </Button>
                  </div>
                </Form>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};

export default ContactUs;